import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/index.js';
import { Screen } from './Screen.js';
import { SignInPage } from '@/features/auth/SignInPage.js';
import { NotFoundPage } from './NotFoundPage.js';
import { RouteError } from './RouteError.js';
import { RedirectIfAuthenticated, RequireAuth } from './RequireAuth.js';

/**
 * Feature screens load on demand.
 *
 * Sign-in is imported eagerly because it is the first thing most visits need;
 * everything else is behind authentication, so there is no reason to ship the
 * profile form's date pickers and tabs to somebody who has not signed in yet.
 */
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage.js').then((m) => ({ default: m.DashboardPage })),
);
const ProfilePage = lazy(() =>
  import('@/features/profile/ProfilePage.js').then((m) => ({ default: m.ProfilePage })),
);
const EmployeesPage = lazy(() =>
  import('@/features/admin/EmployeesPage.js').then((m) => ({ default: m.EmployeesPage })),
);
const LeavePage = lazy(() =>
  import('@/features/leave/LeavePage.js').then((m) => ({ default: m.LeavePage })),
);
const AttendancePage = lazy(() =>
  import('@/features/attendance/AttendancePage.js').then((m) => ({ default: m.AttendancePage })),
);
const ApprovalsPage = lazy(() =>
  import('@/features/attendance/ApprovalsPage.js').then((m) => ({ default: m.ApprovalsPage })),
);
const HolidaysPage = lazy(() =>
  import('@/features/calendar/HolidaysPage.js').then((m) => ({ default: m.HolidaysPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/admin/console/SettingsPage.js').then((m) => ({ default: m.SettingsPage })),
);
const ConsolePage = lazy(() =>
  import('@/features/admin/console/ConsolePage.js').then((m) => ({ default: m.ConsolePage })),
);
const ReportsPage = lazy(() =>
  import('@/features/admin/console/ReportsPage.js').then((m) => ({ default: m.ReportsPage })),
);
const AuditPage = lazy(() =>
  import('@/features/admin/console/AuditPage.js').then((m) => ({ default: m.AuditPage })),
);
const SeparationsPage = lazy(() =>
  import('@/features/admin/console/SeparationsPage.js').then((m) => ({
    default: m.SeparationsPage,
  })),
);
const AnnouncementsPage = lazy(() =>
  import('@/features/announcements/AnnouncementsPage.js').then((m) => ({
    default: m.AnnouncementsPage,
  })),
);
const ComposeAnnouncementPage = lazy(() =>
  import('@/features/announcements/ComposeAnnouncementPage.js').then((m) => ({
    default: m.ComposeAnnouncementPage,
  })),
);
const NotificationsPage = lazy(() =>
  import('@/features/notifications/NotificationsPage.js').then((m) => ({
    default: m.NotificationsPage,
  })),
);
const PoliciesPage = lazy(() =>
  import('@/features/policies/PoliciesPage.js').then((m) => ({ default: m.PoliciesPage })),
);
const ManagePoliciesPage = lazy(() =>
  import('@/features/policies/ManagePoliciesPage.js').then((m) => ({
    default: m.ManagePoliciesPage,
  })),
);
const ReportConcernPage = lazy(() =>
  import('@/features/posh/ReportConcernPage.js').then((m) => ({ default: m.ReportConcernPage })),
);
const CommitteePage = lazy(() =>
  import('@/features/posh/CommitteePage.js').then((m) => ({ default: m.CommitteePage })),
);
const ManageCalendarPage = lazy(() =>
  import('@/features/calendar/ManageCalendarPage.js').then((m) => ({
    default: m.ManageCalendarPage,
  })),
);
const DirectoryPage = lazy(() =>
  import('@/features/directory/DirectoryPage.js').then((m) => ({ default: m.DirectoryPage })),
);
const TeamPage = lazy(() =>
  import('@/features/directory/TeamPage.js').then((m) => ({ default: m.TeamPage })),
);
const ChangeRequestsPage = lazy(() =>
  import('@/features/admin/ChangeRequestsPage.js').then((m) => ({
    default: m.ChangeRequestsPage,
  })),
);
const SecurityPage = lazy(() =>
  import('@/features/settings/SecurityPage.js').then((m) => ({ default: m.SecurityPage })),
);
const InvitationPage = lazy(() =>
  import('@/features/auth/InvitationPage.js').then((m) => ({ default: m.InvitationPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/ForgotPasswordPage.js').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/ResetPasswordPage.js').then((m) => ({ default: m.ResetPasswordPage })),
);

export const router = createBrowserRouter([
  {
    path: '/sign-in',
    errorElement: <RouteError />,
    element: (
      <RedirectIfAuthenticated>
        <SignInPage />
      </RedirectIfAuthenticated>
    ),
  },
  {
    // Reached from an emailed link, so it must work whether or not somebody is
    // already signed in — a manager setting up a new starter's laptop, for
    // instance. Not wrapped in RedirectIfAuthenticated for that reason.
    path: '/invitation',
    errorElement: <RouteError />,
    element: (
      <Screen>
        <InvitationPage />
      </Screen>
    ),
  },
  {
    path: '/forgot-password',
    errorElement: <RouteError />,
    element: (
      <RedirectIfAuthenticated>
        <Screen>
          <ForgotPasswordPage />
        </Screen>
      </RedirectIfAuthenticated>
    ),
  },
  {
    // Also an emailed link: someone who is signed in on one device may still
    // be resetting because they are locked out on another.
    path: '/reset-password',
    errorElement: <RouteError />,
    element: (
      <Screen>
        <ResetPasswordPage />
      </Screen>
    ),
  },
  {
    path: '/',
    errorElement: <RouteError />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <Screen>
            <DashboardPage />
          </Screen>
        ),
      },
      {
        path: 'profile',
        element: (
          <Screen>
            <ProfilePage />
          </Screen>
        ),
      },
      {
        path: 'leave',
        element: (
          <Screen>
            <LeavePage />
          </Screen>
        ),
      },
      {
        path: 'attendance',
        element: (
          <Screen>
            <AttendancePage />
          </Screen>
        ),
      },
      {
        path: 'team/approvals',
        element: (
          <Screen>
            <ApprovalsPage />
          </Screen>
        ),
      },
      {
        path: 'holidays',
        element: (
          <Screen>
            <HolidaysPage />
          </Screen>
        ),
      },
      {
        path: 'admin/console',
        element: (
          <Screen>
            <ConsolePage />
          </Screen>
        ),
      },
      {
        path: 'admin/settings',
        element: (
          <Screen>
            <SettingsPage />
          </Screen>
        ),
      },
      {
        path: 'admin/reports',
        element: (
          <Screen>
            <ReportsPage />
          </Screen>
        ),
      },
      {
        path: 'admin/audit',
        element: (
          <Screen>
            <AuditPage />
          </Screen>
        ),
      },
      {
        path: 'admin/separations',
        element: (
          <Screen>
            <SeparationsPage />
          </Screen>
        ),
      },
      {
        path: 'announcements',
        element: (
          <Screen>
            <AnnouncementsPage />
          </Screen>
        ),
      },
      {
        path: 'admin/announcements',
        element: (
          <Screen>
            <ComposeAnnouncementPage />
          </Screen>
        ),
      },
      {
        path: 'notifications',
        element: (
          <Screen>
            <NotificationsPage />
          </Screen>
        ),
      },
      {
        path: 'policies',
        element: (
          <Screen>
            <PoliciesPage />
          </Screen>
        ),
      },
      {
        path: 'admin/policies',
        element: (
          <Screen>
            <ManagePoliciesPage />
          </Screen>
        ),
      },
      {
        path: 'report-a-concern',
        element: (
          <Screen>
            <ReportConcernPage />
          </Screen>
        ),
      },
      {
        path: 'posh/cases',
        element: (
          <Screen>
            <CommitteePage />
          </Screen>
        ),
      },
      {
        path: 'admin/holidays',
        element: (
          <Screen>
            <ManageCalendarPage />
          </Screen>
        ),
      },
      {
        path: 'directory',
        element: (
          <Screen>
            <DirectoryPage />
          </Screen>
        ),
      },
      {
        path: 'team',
        element: (
          <Screen>
            <TeamPage />
          </Screen>
        ),
      },
      {
        path: 'admin/employees',
        element: (
          <Screen>
            <EmployeesPage />
          </Screen>
        ),
      },
      {
        path: 'admin/change-requests',
        element: (
          <Screen>
            <ChangeRequestsPage />
          </Screen>
        ),
      },
      {
        path: 'settings/security',
        element: (
          <Screen>
            <SecurityPage />
          </Screen>
        ),
      },
      // Feature routes are registered here as they are built, e.g.
      //   { path: 'leave/*', element: <LeaveRoutes /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace />, errorElement: <RouteError /> },
]);
